/**
 * What a bill design is made of.
 *
 * A design used to be a fixed set of yes/no flags — `showAddress`, `showGstin`
 * and so on. That let a shop turn my lines on and off and nothing else: they
 * could not add a line of their own, could not reorder anything, and could not
 * have a field I had not thought of. Every new idea meant a code change.
 *
 * A design is now an ordered list of fields the shop composes. Adding a field
 * type here is the only place it needs to exist; the composer draws it and all
 * three printing paths get it at once.
 */

/** The field types a design can be built from, in the order they are offered. */
export const FIELD_TYPES = {
  // ── From the organization ────────────────────────────────────────────────
  // Each of these prints what the organization record holds, unless the design
  // types something else in. A shop trading under a different name from the one
  // it was registered with had no way to put that on its own bill.
  shopName: {
    label: 'Shop name', group: 'Shop', text: true,
    textLabel: 'Name to print',
    textPlaceholder: 'Leave empty to use your organization name',
    defaults: { align: 'center', big: true, bold: true },
  },
  branch: {
    label: 'Branch name', group: 'Shop', text: true,
    textLabel: 'Branch line',
    textPlaceholder: "Leave empty to use the branch's own name",
    defaults: { align: 'center' },
  },
  address: {
    label: 'Shop address', group: 'Shop', text: true,
    textLabel: 'Address to print',
    textPlaceholder: 'Leave empty to use your saved address',
    defaults: { align: 'center' },
  },
  phone: {
    label: 'Shop phone', group: 'Shop', text: true,
    textLabel: 'Phone line',
    textPlaceholder: 'Leave empty to use your saved phone',
    defaults: { align: 'center' },
  },
  gstin: { label: 'GSTIN', group: 'Shop', defaults: { align: 'center' } },

  // ── From the bill ───────────────────────────────────────────────────────
  title: { label: 'Title line', group: 'Bill', text: true, defaults: { align: 'center', bold: true, text: 'RECEIPT' } },
  billNo: { label: 'Bill number', group: 'Bill', defaults: { align: 'center' } },
  date: { label: 'Date', group: 'Bill', defaults: { align: 'center' } },
  time: { label: 'Time', group: 'Bill', defaults: { align: 'center' } },
  customer: { label: 'Customer name', group: 'Bill', defaults: { align: 'left' } },
  customerPhone: { label: 'Customer phone', group: 'Bill', defaults: { align: 'left' } },
  paymentMode: { label: 'Payment mode', group: 'Bill', defaults: { align: 'left' } },
  orderType: { label: 'Order type', group: 'Bill', defaults: { align: 'left' } },
  deliveryAddress: { label: 'Delivery address', group: 'Bill', defaults: { align: 'left' } },
  servedBy: { label: 'Served by', group: 'Bill', defaults: { align: 'left' } },

  // ── The table and the money ─────────────────────────────────────────────
  items: {
    label: 'Item table',
    group: 'Items',
    columns: true,
    defaults: { columns: ['qty', 'amount'] },
  },
  itemCount: {
    label: 'Item count',
    group: 'Items',
    countMode: true,
    text: true,
    defaults: { align: 'right', mode: 'lines', text: 'Total items' },
  },
  totals: {
    label: 'Totals',
    group: 'Items',
    parts: true,
    // Round off sits between the tax and the total, where the arithmetic
    // happens. It prints only when there are paise to account for, so a shop
    // pricing in whole rupees never sees it.
    defaults: { align: 'right', parts: ['subtotal', 'cgst', 'sgst', 'roundOff', 'total'] },
  },

  // ── Anything else ───────────────────────────────────────────────────────
  qr: {
    label: 'QR code',
    group: 'Extra',
    code: true,
    codeLabel: 'Link the QR opens',
    codePlaceholder: 'https://yourshop.com/return?bill={billNo}',
    defaults: { align: 'center', url: '', text: 'Scan to return an item' },
  },
  // A barcode holds a short code a scanner reads back — a bill number, most
  // often, so a return or an exchange can be found without typing it. A QR can
  // hold a whole link; CODE128 holds plain characters and nothing else.
  barcode: {
    label: 'Barcode',
    group: 'Extra',
    code: true,
    codeLabel: 'What the barcode holds',
    codePlaceholder: '{billNo}',
    defaults: { align: 'center', url: '{billNo}', text: '' },
  },
  // The answer to "there is no field for my PAN / licence / invoice number".
  // Anything a shop needs that is not on this list is one of these with the
  // label typed in — a field type per document a trade might carry would be a
  // list nobody could read and still miss the next one.
  text: {
    label: 'Your own line',
    group: 'Extra',
    text: true,
    hint: 'Type anything — "PAN: AAAAA0000A", a licence number, a note. Use {billNo} and the rest for values that change per bill.',
    defaults: { align: 'center', text: '' },
  },
  divider: { label: 'Dashed line', group: 'Extra', defaults: {} },
  blank: { label: 'Blank line', group: 'Extra', defaults: {} },
};

/** The columns an item table can show, and how wide each one is. */
export const ITEM_COLUMNS = {
  qty: { label: 'Qty', width: 'qty' },
  rate: { label: 'Rate', width: 'amt' },
  amount: { label: 'Amount', width: 'amt' },
};

/** The lines a totals block can show. */
export const TOTAL_PARTS = {
  subtotal: 'Subtotal',
  cgst: 'CGST',
  sgst: 'SGST',
  igst: 'IGST',
  roundOff: 'Round off',
  total: 'TOTAL',
};

/** Placeholders a shop can put in their own line, and what fills them in. */
export const TEXT_TOKENS = [
  '{shop}', '{branch}', '{billNo}', '{date}', '{time}', '{customer}',
  '{phone}', '{staff}', '{orderType}', '{items}', '{qty}', '{subtotal}', '{total}',
];

let seq = 0;
/** Ids only need to be unique within one design, and never leave it. */
export const newFieldId = () => `f${Date.now().toString(36)}${(seq += 1).toString(36)}`;

export const makeField = (type, overrides = {}) => ({
  id: newFieldId(),
  type,
  on: true,
  ...(FIELD_TYPES[type]?.defaults || {}),
  ...overrides,
});

/**
 * Turn a design written with the old yes/no flags into a field list.
 *
 * Every design saved before this existed — and every branch pointing at one —
 * has to keep printing exactly what it printed yesterday, so the order here
 * mirrors what `buildReceiptData` used to emit line for line.
 */
export const fieldsFromFlags = (s = {}) => {
  const f = [];
  const add = (type, o) => f.push(makeField(type, o));

  add('shopName');
  if (s.showBranch) add('branch');
  if (s.showAddress) add('address');
  if (s.showPhone) add('phone');
  if (s.showGstin) add('gstin');
  if (s.title) add('title', { text: s.title });
  if (s.showBillNo !== false) add('billNo');
  if (s.showDate !== false) add('date');
  if (s.showTime) add('time');
  add('blank');
  if (s.showCustomer) add('customer');
  if (s.showPaymentMode) add('paymentMode');
  if (s.showDeliveryAddress) {
    add('customerPhone');
    add('deliveryAddress');
  }
  add('divider');
  add('items', { columns: s.showPrices === false ? ['qty'] : ['qty', 'amount'] });
  add('divider');
  if (s.showPrices !== false) {
    const parts = ['subtotal'];
    if (s.showTaxBreakdown) parts.push('cgst', 'sgst', 'igst');
    parts.push('roundOff', 'total');
    add('totals', { parts });
  }
  add('blank');
  if (s.footerLine1) add('text', { text: s.footerLine1 });
  if (s.footerLine2) add('text', { text: s.footerLine2 });
  return f;
};

/**
 * The field list for a design, whichever way it was written.
 *
 * Designs saved before the builder existed have flags and no `fields`; they are
 * converted on read rather than migrated in the database, so an older app
 * version reading the same row still finds the flags it expects.
 */
export const fieldsOf = (settings = {}) =>
  Array.isArray(settings.fields) && settings.fields.length
    ? settings.fields
    : fieldsFromFlags(settings);

/**
 * Fill the shop lines with what they would print, so the editor shows the words
 * rather than a grey hint of them.
 *
 * Only the organization's own details. The branch line is deliberately left
 * empty: it changes with the bill, and writing today's branch into the design
 * would make every branch print that one name.
 */
export const withShopText = (fields, org = {}) => {
  const value = {
    shopName: org.name,
    address: org.address,
    phone: org.phone || org.mobileNumber,
  };
  return fields.map((f) => (
    !f.text && value[f.type] ? { ...f, text: value[f.type] } : f
  ));
};
